import { Injectable, computed, inject, signal } from '@angular/core';
import { EMPTY, catchError, finalize, of, tap } from 'rxjs';

import { NotificationItem } from '@app/core/models';
import { NotificationsApiService } from '@app/features/ops/notifications-api.service';
import { parseApiError } from '@app/shared/utils/api-error.utils';

@Injectable({ providedIn: 'root' })
export class NotificationStoreService {
  private readonly notificationsApi = inject(NotificationsApiService);
  private readonly notificationsState = signal<NotificationItem[]>([]);
  private readonly markAllReadPendingState = signal(false);
  private readonly pendingNotificationIdsState = signal<string[]>([]);
  private readonly dismissingNotificationIdsState = signal<string[]>([]);
  private readonly errorState = signal('');
  private readonly removalDelayMs = 220;

  readonly notifications = computed(() => this.notificationsState());
  readonly visibleNotifications = computed(() =>
    this.notificationsState().filter(
      (notification) => !notification.read || this.dismissingNotificationIdsState().includes(notification.id)
    )
  );
  readonly unreadCount = computed(
    () => this.notificationsState().filter((notification) => !notification.read).length
  );
  readonly markAllReadPending = computed(() => this.markAllReadPendingState());
  readonly error = computed(() => this.errorState());

  load() {
    return this.notificationsApi
      .getNotifications()
      .pipe(
        tap((notifications) => {
          this.notificationsState.set(notifications);
          this.errorState.set('');
        }),
        catchError((error) => {
          this.errorState.set(parseApiError(error, 'Unable to load notifications right now.').message);
          return of<NotificationItem[]>([]);
        })
      );
  }

  markAllRead(): void {
    if (this.markAllReadPendingState()) {
      return;
    }

    const unreadIds = this.notificationsState()
      .filter((notification) => !notification.read)
      .map((notification) => notification.id);

    if (!unreadIds.length) {
      return;
    }

    this.markAllReadPendingState.set(true);
    this.errorState.set('');

    this.notificationsApi
      .markAllRead()
      .pipe(
        finalize(() => this.markAllReadPendingState.set(false)),
        catchError((error) => {
          this.errorState.set(parseApiError(error, 'Unable to mark all notifications as read right now.').message);
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.notificationsState.update((notifications) =>
          notifications.map((notification) => ({ ...notification, read: true }))
        );
        this.animateNotificationRemoval(unreadIds);
      });
  }

  markRead(notificationId: string): void {
    if (!notificationId || this.pendingNotificationIdsState().includes(notificationId)) {
      return;
    }

    this.pendingNotificationIdsState.update((ids) => [...ids, notificationId]);
    this.errorState.set('');

    this.notificationsApi
      .markRead(notificationId)
      .pipe(
        finalize(() =>
          this.pendingNotificationIdsState.update((ids) => ids.filter((id) => id !== notificationId))
        ),
        catchError((error) => {
          this.errorState.set(parseApiError(error, 'Unable to mark this notification as read right now.').message);
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.notificationsState.update((notifications) =>
          notifications.map((notification) =>
            notification.id === notificationId ? { ...notification, read: true } : notification
          )
        );
        this.animateNotificationRemoval([notificationId]);
      });
  }

  isNotificationPending(notificationId: string): boolean {
    return this.pendingNotificationIdsState().includes(notificationId);
  }

  isNotificationDismissing(notificationId: string): boolean {
    return this.dismissingNotificationIdsState().includes(notificationId);
  }

  private animateNotificationRemoval(notificationIds: string[]): void {
    if (!notificationIds.length) {
      return;
    }

    this.dismissingNotificationIdsState.update((ids) => Array.from(new Set([...ids, ...notificationIds])));

    setTimeout(() => {
      this.notificationsState.update((notifications) =>
        notifications.filter((notification) => !notification.read || !notificationIds.includes(notification.id))
      );
      this.dismissingNotificationIdsState.update((ids) =>
        ids.filter((id) => !notificationIds.includes(id))
      );
    }, this.removalDelayMs);
  }
}
